import { useState } from "react";
import { GraduationCap, Rocket, Briefcase, Palette, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const personas = [
  { icon: GraduationCap, label: "Students", tools: ["Math Professor", "Language Translator", "College Ready"], description: "Study smarter with AI tutoring, translation, and college prep tools." },
  { icon: Rocket, label: "Founders", tools: ["Chatbot Builder", "SEO Analyzer", "Blog Assistant"], description: "Launch faster with AI-powered content, customer support, and marketing." },
  { icon: Briefcase, label: "Professionals", tools: ["Contract Analyzer", "Invoice Generator", "Grammar Assistant"], description: "Streamline legal review, billing, and business communications." },
  { icon: Palette, label: "Creators", tools: ["AI Image Generator", "PDF StoryBook", "Prompt Studio"], description: "Generate images, write stories, and craft perfect prompts." },
  { icon: User, label: "Everyday Users", tools: ["Personal Chef", "TravelGPT", "Current Weather"], description: "Get daily help with recipes, travel planning, and quick lookups." },
];

export const UseCasePersonas = () => {
  const [active, setActive] = useState(0);

  return (
    <section className="py-20 px-4" aria-labelledby="personas-heading">
      <div className="max-w-5xl mx-auto">
        <h2 id="personas-heading" className="text-3xl md:text-4xl font-bold text-center mb-3">Built for You</h2>
        <p className="text-muted-foreground text-center mb-10 max-w-lg mx-auto">
          No matter who you are, GenieBot has tools that fit your workflow.
        </p>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" role="tablist" aria-label="User personas">
          {personas.map((p, i) => {
            const Icon = p.icon;
            return (
              <button
                key={p.label}
                onClick={() => setActive(i)}
                role="tab"
                aria-selected={active === i}
                aria-controls={`persona-panel-${i}`}
                id={`persona-tab-${i}`}
                className={cn(
                  "flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none",
                  active === i
                    ? "bg-primary text-primary-foreground shadow-md"
                    : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
                )}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                <span className="hidden min-[400px]:inline">{p.label}</span>
                <span className="min-[400px]:hidden sr-only">{p.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            id={`persona-panel-${active}`}
            role="tabpanel"
            aria-labelledby={`persona-tab-${active}`}
            className="p-6 sm:p-8 rounded-2xl border border-border bg-card max-w-2xl mx-auto text-center"
          >
            <h3 className="font-semibold text-lg mb-2">{personas[active].label}</h3>
            <p className="text-muted-foreground mb-5">{personas[active].description}</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {personas[active].tools.map((tool) => (
                <span key={tool} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
                  {tool}
                </span>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
